/**
 * CalendarView — month grid with category dots per day.
 *
 * Pixel-faithful port of the calendar grid in `design/project/screens-calendar.jsx`.
 *
 * Behaviour:
 *   - Month is controlled via `cursor` (a `Date` whose Y/M anchors the grid).
 *   - For each day with transactions, we render up to 3 colored dots — one
 *     per top category (ranked by total amount in that day).
 *   - Tapping a day fires `onSelectDate(date: 'YYYY-MM-DD')` AND expands an
 *     inline list of that day's transactions BELOW the grid.
 *
 * Sharp edges:
 *   - We never auto-convert currencies for the per-day total. We render
 *     ONLY the count when transactions span >1 currency on the same day —
 *     summing across currencies would silently mis-sum (TWD + JPY = ?).
 *   - A day with zero transactions renders no dot row (no empty placeholder).
 */
import { useMemo } from 'react';
import type { CategoryRow, TransactionRow } from '@/lib/sheets/types';
import { formatAmount } from '@/lib/format/currency';
import Icon, { type IconName } from '@/components/common/Icon';
import { cssColor } from '@/lib/colorToken';
import { dayPart } from '@/lib/format/datetime';
import { displayTxName } from '@/lib/displayTxName';
import { displayTxIcon } from '@/lib/displayTxIcon';

export interface CalendarViewProps {
  cursor: Date;
  transactions: TransactionRow[];
  categories: CategoryRow[];
  /** Primary display currency — when supplied, the day-detail rows show
   *  amounts FX-converted into this currency (the native amount lives on
   *  a 「原幣」 sub-line inside `RecentTxList`-style rendering, but the
   *  calendar's compact day-detail row keeps just the converted value). */
  primaryCurrency?: string;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  onTxClick?: (tx: TransactionRow) => void;
}

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'] as const;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function ymd(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

interface DayBucket {
  total: number;
  byCat: Map<string, number>;
}

function bucketize(
  txs: TransactionRow[],
  year: number,
  month: number,
): Map<number, DayBucket> {
  const out = new Map<number, DayBucket>();
  const prefix = `${year}-${pad2(month)}-`;
  for (const t of txs) {
    if (!t.date.startsWith(prefix) || t.deleted_at) continue;
    const day = Number(t.date.slice(8, 10));
    const bucket = out.get(day) ?? { total: 0, byCat: new Map() };
    bucket.total += Math.abs(t.amount_minor);
    bucket.byCat.set(
      t.category_id,
      (bucket.byCat.get(t.category_id) ?? 0) + Math.abs(t.amount_minor),
    );
    out.set(day, bucket);
  }
  return out;
}

export default function CalendarView({
  cursor,
  transactions,
  categories,
  primaryCurrency,
  selectedDate,
  onSelectDate,
  onTxClick,
}: CalendarViewProps): JSX.Element {
  // Silence unused-prop warning until the day-detail row is refactored to
  // use it. Keeping the prop in the public signature now means the App-
  // level wiring is forward-compatible with the upcoming redesign.
  void primaryCurrency;
  const year = cursor.getFullYear();
  const month = cursor.getMonth() + 1; // 1-12

  const firstDow = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month, 0).getDate();

  const buckets = useMemo(
    () => bucketize(transactions, year, month),
    [transactions, year, month],
  );

  const catById = useMemo(() => {
    const m = new Map<string, CategoryRow>();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const today = new Date();
  const isThisMonth =
    today.getFullYear() === year && today.getMonth() + 1 === month;

  const selDate = selectedDate;
  const selectedTx = useMemo(() => {
    if (!selDate) return [];
    // v5: `t.date` may be `YYYY-MM-DD` or `YYYY-MM-DDTHH:MM`; the
    // calendar always passes a day-only `selDate`. Compare via dayPart
    // so imported rows with `T11:51`-style stamps still match.
    return transactions
      .filter((t) => dayPart(t.date) === selDate && !t.deleted_at)
      .sort((a, b) => (a.created_at < b.created_at ? -1 : 1));
  }, [transactions, selDate]);

  return (
    <div data-testid="calendar-view">
      {/* Weekday header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          padding: '4px 12px',
          gap: 2,
        }}
      >
        {WEEKDAYS.map((d, i) => (
          <div
            key={d}
            style={{
              textAlign: 'center',
              font: '500 11px var(--font-cjk)',
              color:
                i === 0 || i === 6 ? 'var(--clay-500)' : 'var(--fg-3)',
              letterSpacing: '0.06em',
            }}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          padding: '6px 12px',
          gap: 2,
        }}
      >
        {cells.map((d, i) => {
          if (d === null)
            return <div key={`empty-${i}`} style={{ height: 48 }} />;
          const bucket = buckets.get(d);
          const dots = bucket
            ? [...bucket.byCat.entries()]
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([catId]) => cssColor(catById.get(catId)?.color_token))
            : [];
          const ds = ymd(year, month, d);
          const sel = ds === selDate;
          const isToday = isThisMonth && d === today.getDate();
          return (
            <button
              key={ds}
              type="button"
              aria-label={`${year} 年 ${month} 月 ${d} 日${bucket ? `，${bucket.byCat.size} 個分類有紀錄` : '，無紀錄'}`}
              aria-pressed={sel}
              data-testid={`cal-day-${ds}`}
              onClick={() => onSelectDate(ds)}
              style={{
                height: 48,
                background: 'transparent',
                border: sel ? '2px solid var(--fg-1)' : '2px solid transparent',
                borderRadius: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                cursor: 'pointer',
                padding: 0,
                color: sel
                  ? 'var(--fg-1)'
                  : isToday
                    ? 'var(--accent)'
                    : 'var(--fg-1)',
              }}
            >
              <div
                style={{
                  font: `${sel || isToday ? 600 : 400} 15px var(--font-num)`,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {d}
              </div>
              <div style={{ display: 'flex', gap: 2, height: 5 }}>
                {dots.map((c, j) => (
                  <span
                    key={`${ds}-d${j}`}
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: 2.5,
                      background: c,
                    }}
                  />
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected day inline list */}
      {selDate && (
        <div
          data-testid="cal-day-detail"
          style={{
            padding: '14px 16px 24px',
            background: 'var(--cream-100)',
            borderTop: '0.5px solid var(--border-1)',
            marginTop: 8,
          }}
        >
          <div
            style={{
              font: '600 14px var(--font-cjk)',
              color: 'var(--fg-1)',
              padding: '0 0 8px',
            }}
          >
            {selDate.replace(/-/g, '/')}
          </div>
          {selectedTx.length === 0 ? (
            <div
              style={{
                background: 'var(--bg-surface)',
                border: '1px dashed var(--border-2)',
                borderRadius: 12,
                padding: '24px 16px',
                textAlign: 'center',
                color: 'var(--fg-3)',
                font: '500 13px var(--font-cjk)',
              }}
            >
              這天沒有紀錄
            </div>
          ) : (
            <div
              style={{
                background: 'var(--bg-surface)',
                borderRadius: 14,
                padding: '4px 14px',
                boxShadow: 'var(--shadow-1)',
              }}
            >
              {selectedTx.map((t, i) => {
                const c = catById.get(t.category_id);
                const sub = t.subcategory_id ? catById.get(t.subcategory_id) : undefined;
                const isInc = t.kind === 'income';
                const icon = displayTxIcon(c, sub) as IconName;
                const primary = displayTxName(t, c, sub);
                return (
                  <button
                    key={t.id}
                    type="button"
                    aria-label={`${primary === '—' ? '紀錄' : primary}，${isInc ? '收入' : '支出'} ${formatAmount(t.amount_minor, t.currency)}`}
                    onClick={onTxClick ? () => onTxClick(t) : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 0',
                      width: '100%',
                      background: 'transparent',
                      border: 0,
                      borderBottom:
                        i < selectedTx.length - 1
                          ? '0.5px solid var(--border-1)'
                          : 0,
                      cursor: onTxClick ? 'pointer' : 'default',
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 10,
                        background: cssColor(c?.color_token),
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      <Icon name={icon} size={16} stroke="#fff" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          font: '500 14px var(--font-cjk)',
                          color: 'var(--fg-1)',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {primary}
                      </div>
                    </div>
                    <div
                      style={{
                        font: '600 14px var(--font-num)',
                        fontVariantNumeric: 'tabular-nums',
                        color: isInc ? 'var(--income)' : 'var(--fg-1)',
                      }}
                    >
                      {isInc ? '+' : '-'}
                      {formatAmount(t.amount_minor, t.currency)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
